import type { AdventureContent, AdventureOption, PlacedThought } from "../types/adventure";
import type { ChallengeMode } from "./ChallengePanel";
import { ChallengeAlert, ChallengeConsole } from "./ChallengePanel";
import { CockpitFrame } from "./CockpitFrame";
import { GameTimerBar } from "./GameTimerBar";
import { HelpfulDashboard } from "./HelpfulDashboard";
import { PositionDebugMarkers, PositionDebugRects } from "./PositionDebugOverlay";
import { ThoughtCloud } from "./ThoughtCloud";

interface GameStageProps {
  adventure: AdventureContent;
  images: Record<string, string | null>;
  thoughts: PlacedThought[];
  revealedCount: number;
  roundNumber: number;
  phase: string;
  timeLeft: number;
  showTimer: boolean;
  selectMode: boolean;
  focusMode: boolean;
  selectedHelpfulIds: string[];
  onToggleHelpful?: (thoughtId: string) => void;
  challenge?: {
    mode: ChallengeMode;
    text: string;
    options: [AdventureOption, AdventureOption];
    blocked: boolean;
    lastOutcome: string | null;
    lastBest: boolean;
    onChoose: (option: AdventureOption) => void;
    showOptions?: boolean;
  };
  selectPrompt?: string;
  showPositionDebug?: boolean;
  inReveal?: boolean;
  onThoughtSettled?: (thoughtIndex: number) => void;
}

export function GameStage({
  adventure,
  images,
  thoughts,
  revealedCount,
  roundNumber,
  phase,
  timeLeft,
  showTimer,
  selectMode,
  focusMode,
  selectedHelpfulIds,
  onToggleHelpful,
  challenge,
  selectPrompt,
  showPositionDebug = false,
  inReveal = false,
  onThoughtSettled,
}: GameStageProps) {
  const systemMap = Object.fromEntries(
    adventure.systems.map((s) => [s.id, s])
  );

  const showAllThoughts = selectMode || focusMode;
  const visibleThoughts = thoughts
    .map((t, index) => ({ thought: t, index }))
    .filter(({ index }) => showAllThoughts || index < revealedCount);

  const activeSystems = new Set(
    visibleThoughts.map(({ thought }) => thought.system)
  );

  const showDashboard = selectMode && adventure.showCockpit !== false;
  const immersive = adventure.showCockpit === false;

  return (
    <div
      className={[
        immersive ? "act-stage act-stage--immersive" : "act-stage",
        focusMode ? "act-stage--focus" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showTimer && <GameTimerBar timeLeft={timeLeft} />}

      <div className="act-cockpit-wrap">
        <CockpitFrame
          adventure={adventure}
          images={images}
          roundNumber={roundNumber}
          phase={phase}
        />

        {showDashboard && (
          <HelpfulDashboard
            selectedIds={selectedHelpfulIds}
            thoughts={thoughts}
            systemMap={systemMap}
            focusMode={focusMode}
          />
        )}

        {challenge?.showOptions && (
          <ChallengeConsole
            mode={challenge.mode}
            options={challenge.options}
            blocked={challenge.blocked}
            onChoose={challenge.onChoose}
          />
        )}

        {showPositionDebug && <PositionDebugRects />}

        <div className="act-thoughts-layer" aria-label="Gedachten">
          {showPositionDebug && <PositionDebugMarkers />}

          {visibleThoughts.map(({ thought, index }) => {
            const sys = systemMap[thought.system];
            const isSelected = selectedHelpfulIds.includes(thought.thoughtId);
            const pinSlot = isSelected
              ? selectedHelpfulIds.indexOf(thought.thoughtId)
              : -1;
            const isLatestRevealed = inReveal && index === revealedCount - 1;

            return (
              <ThoughtCloud
                key={thought.thoughtId}
                thought={thought}
                systemLabel={sys?.label ?? thought.system}
                systemIcon={sys?.icon ?? "💭"}
                selectable={selectMode && thought.helpful}
                selected={isSelected}
                faded={focusMode && !isSelected}
                pinned={focusMode && isSelected}
                pinSlot={pinSlot}
                onToggleSelect={onToggleHelpful}
                onSettled={
                  isLatestRevealed && onThoughtSettled
                    ? () => onThoughtSettled(index)
                    : undefined
                }
              />
            );
          })}
        </div>

        {selectMode && selectPrompt ? (
          <ChallengeAlert mode="defuse" challenge={selectPrompt} lastOutcome={null} />
        ) : (
          challenge && (
            <ChallengeAlert
              mode={challenge.mode}
              challenge={challenge.text}
              lastOutcome={challenge.lastOutcome}
              lastBest={challenge.lastBest}
            />
          )
        )}
      </div>

      <div className="act-systems" aria-label="Actieve systemen">
        {adventure.systems.map((sys) => (
          <div
            key={sys.id}
            className={
              activeSystems.has(sys.id)
                ? "act-system act-system--active"
                : "act-system"
            }
            title={sys.label}
          >
            <span className="act-system__icon" aria-hidden>
              {sys.icon}
            </span>
            <span className="act-system__label">{sys.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
